/**
 * KidSafeSeal — world by me's kidSAFE Seal Program membership seal, drawn by
 * kidSAFE's own script (their rule: never an image copy of the seal).
 *
 * Where it goes (operator, 2026-10-24; the in-app spot re-ruled 2026-10-27): only
 * the pages that explain the app to a grown-up — the landing page on a device we
 * don't recognize (web only), /privacy and /terms, and the bottom of Settings >
 * Legal, under the four document rows, at kidSAFE's own 200px. Never the sign-in
 * screen, never anywhere a child uses: a child session renders nothing, so the
 * script (which sends kidsafe.com the visitor's IP) never loads for a child.
 * The privacy policy's "Who we share information with" names these pages.
 *
 * seals.js renders once: it polls for `#ksp-seals`, fills it, and stops. A
 * single-page app mounts the seal long after the first load, so each mount
 * puts a fresh copy of the script tag on the page and the unmount removes it.
 *
 * The seal links to kidSAFE's member page with target="_blank". In the iPad
 * app that would leave for Safari; a capture-phase click opens it in the
 * in-app browser instead (kidSAFE asks for an in-app view where the script
 * has none). A build without the plugin falls back to the system browser.
 */
import { useEffect, useRef, type CSSProperties } from "react";
import { ChromeTokens } from "../chrome/tokens";
import { useIsChildSession } from "../hooks/useIsChildSession";
import { isNativePlatform } from "../platform";
import { openInAppBrowser } from "../platform/inAppBrowser";

export const KIDSAFE_SCRIPT_ID = "ksp-seals-script";
export const KIDSAFE_SCRIPT_SRC = "https://kidsafe.com/js/seals/seals.js";
export const KIDSAFE_PRODUCT = "zC1v5S7oQmRM79Sk3NJhWtMCfcna4QatYn6atVxo";
/** kidSAFE's short wording, shown beside the seal on the legal pages. */
export const KIDSAFE_WORDING =
  "world by me is a member in the kidSAFE Seal Program. To learn more, click on the seal or go to www.kidsafe.com.";

export interface KidSafeSealProps {
  /** Show kidSAFE's short wording under the seal (the legal pages). */
  wording?: boolean;
  style?: CSSProperties;
}

export function KidSafeSeal({ wording = false, style }: KidSafeSealProps): JSX.Element | null {
  const isChild = useIsChildSession();
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isChild) return;
    document.getElementById(KIDSAFE_SCRIPT_ID)?.remove();
    const script = document.createElement("script");
    script.id = KIDSAFE_SCRIPT_ID;
    script.src = KIDSAFE_SCRIPT_SRC;
    script.async = true;
    script.setAttribute("data-product", KIDSAFE_PRODUCT);
    document.body.appendChild(script);
    return () => script.remove();
  }, [isChild]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box || !isNativePlatform()) return;
    const onClick = (e: MouseEvent) => {
      const link = (e.target as Element | null)?.closest?.("a[href]");
      if (!(link instanceof HTMLAnchorElement)) return;
      e.preventDefault();
      e.stopPropagation();
      const href = link.href;
      void openInAppBrowser(href).then((opened) => {
        if (!opened) window.open(href, "_blank", "noopener");
      });
    };
    box.addEventListener("click", onClick, true);
    return () => box.removeEventListener("click", onClick, true);
  }, [isChild]);

  if (isChild) return null;
  return (
    <div
      ref={boxRef}
      data-testid="kidsafe-seal"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, ...style }}
    >
      {/* kidSAFE fills this div; it can hold more than one badge. */}
      <div id="ksp-seals" style={{ minHeight: 64 }} />
      {wording && (
        <p style={{ margin: 0, maxWidth: "min(420px, 100%)", textAlign: "center", fontSize: 12.5, color: ChromeTokens.color.inkMuted, lineHeight: 1.5 }}>
          {KIDSAFE_WORDING}
        </p>
      )}
    </div>
  );
}
